import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { authApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { User, Building2, MapPin, Phone, Mail, Key, Download, Trash2, Shield, Hash, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, refreshUser, logout } = useAuth();
  const [form, setForm] = useState({ name: '', company_name: '', street: '', house_number: '', plz: '', city: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: '', password: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        company_name: user.company_name || '',
        street: user.street || '',
        house_number: user.house_number || '',
        plz: user.plz || '',
        city: user.city || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authApi.updateMe(form);
      await refreshUser();
      toast.success('Profil gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error('Die neuen Passwörter stimmen nicht überein');
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error('Mindestens 6 Zeichen erforderlich');
      return;
    }
    setPwLoading(true);
    try {
      await authApi.changePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Passwort geändert');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
    setPwLoading(false);
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email.includes('@')) {
      toast.error('Ungültige E-Mail-Adresse');
      return;
    }
    setEmailLoading(true);
    try {
      await authApi.changeEmail(emailForm);
      await refreshUser();
      toast.success('E-Mail-Adresse geändert');
      setEmailForm({ new_email: '', password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
    setEmailLoading(false);
  };

  const handleExport = async () => {
    try {
      const res = await authApi.exportData();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `datenexport_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Daten exportiert');
    } catch {
      toast.error('Export fehlgeschlagen');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'LÖSCHEN') return;
    try {
      await authApi.deleteAccount();
      toast.success('Konto gelöscht');
      logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
    setDeleteOpen(false);
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="h-7 w-7 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="profile-heading">Mein Profil</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} data-testid="profile-role-badge">
              <Shield className="h-3 w-3 mr-1" />
              {user.role === 'admin' ? 'Administrator' : 'Benutzer'}
            </Badge>
            {user.customer_number && (
              <Badge variant="outline" data-testid="profile-customer-number">
                <Hash className="h-3 w-3 mr-1" />{user.customer_number}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Stammdaten */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><User className="h-4 w-4" /> Stammdaten</CardTitle>
          <CardDescription>Persönliche Daten und Firmeninformationen</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-name">Name</Label>
                <Input id="p-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Vollständiger Name" data-testid="profile-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-company">Firma</Label>
                <Input id="p-company" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Firmenname" data-testid="profile-company-input" />
              </div>
            </div>
            <Separator />
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><MapPin className="h-4 w-4" /> Adresse</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="p-street">Straße</Label>
                <Input id="p-street" value={form.street} onChange={e => setForm(f => ({ ...f, street: e.target.value }))} data-testid="profile-street-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-hnr">Hausnr.</Label>
                <Input id="p-hnr" value={form.house_number} onChange={e => setForm(f => ({ ...f, house_number: e.target.value }))} data-testid="profile-housenr-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-plz">PLZ</Label>
                <Input id="p-plz" value={form.plz} onChange={e => setForm(f => ({ ...f, plz: e.target.value }))} data-testid="profile-plz-input" />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="p-city">Ort</Label>
                <Input id="p-city" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} data-testid="profile-city-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-phone"><Phone className="h-3.5 w-3.5 inline mr-1" />Telefon</Label>
                <Input id="p-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="030-1234567" data-testid="profile-phone-input" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={saving} data-testid="profile-save-btn">
                {saving ? 'Speichern...' : 'Profil speichern'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* E-Mail ändern */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4" /> E-Mail-Adresse</CardTitle>
          <CardDescription>Aktuelle E-Mail: <strong>{user.email}</strong></CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEmailChange} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-newemail">Neue E-Mail</Label>
                <Input id="p-newemail" type="email" value={emailForm.new_email} onChange={e => setEmailForm(f => ({ ...f, new_email: e.target.value }))} placeholder="neue@email.de" data-testid="profile-newemail-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-emailpw">Passwort bestätigen</Label>
                <Input id="p-emailpw" type="password" value={emailForm.password} onChange={e => setEmailForm(f => ({ ...f, password: e.target.value }))} placeholder="Aktuelles Passwort" data-testid="profile-emailpw-input" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" variant="outline" disabled={emailLoading || !emailForm.new_email || !emailForm.password} data-testid="profile-email-save-btn">
                {emailLoading ? 'Ändern...' : 'E-Mail ändern'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Passwort ändern */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Key className="h-4 w-4" /> Passwort ändern</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-curpw">Aktuelles Passwort</Label>
                <Input id="p-curpw" type="password" value={pwForm.current_password} onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} data-testid="profile-curpw-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-newpw">Neues Passwort</Label>
                <Input id="p-newpw" type="password" value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} data-testid="profile-newpw-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-confpw">Bestätigen</Label>
                <Input id="p-confpw" type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} data-testid="profile-confpw-input" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" variant="outline" disabled={pwLoading || !pwForm.current_password || !pwForm.new_password} data-testid="profile-pw-save-btn">
                {pwLoading ? 'Ändern...' : 'Passwort ändern'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Login-Protokoll */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Clock className="h-4 w-4" /> Letzte Anmeldungen</CardTitle>
          <CardDescription>Die letzten 10 Anmeldungen an Ihrem Konto</CardDescription>
        </CardHeader>
        <CardContent>
          {user.login_history && user.login_history.length > 0 ? (
            <div className="space-y-2">
              {[...user.login_history].reverse().slice(0, 10).map((entry, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0" data-testid={`login-entry-${i}`}>
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full ${i === 0 ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                    <span className="text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground font-mono">{entry.ip}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Noch keine Anmeldungen protokolliert.</p>
          )}
        </CardContent>
      </Card>

      {/* DSGVO */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Shield className="h-4 w-4" /> Datenschutz (DSGVO)</CardTitle>
          <CardDescription>Exportieren oder löschen Sie Ihre Daten</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={handleExport} data-testid="profile-export-btn">
              <Download className="h-4 w-4 mr-2" /> Daten exportieren (JSON)
            </Button>
            <Button variant="destructive" onClick={() => setDeleteOpen(true)} data-testid="profile-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Konto löschen
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Beim Löschen werden alle Ihre Daten und zugehörigen Analysen unwiderruflich entfernt.
          </p>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konto wirklich löschen?</DialogTitle>
            <DialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Alle Ihre Daten und Analysen werden dauerhaft gelöscht.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Tippen Sie <strong>LÖSCHEN</strong> zur Bestätigung</Label>
            <Input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="LÖSCHEN" data-testid="profile-delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); }}>Abbrechen</Button>
            <Button variant="destructive" disabled={deleteConfirm !== 'LÖSCHEN'} onClick={handleDeleteAccount} data-testid="profile-delete-confirm-btn">
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
